/**
 * StewSignFlow
 *
 * Single-officer inline signing for a Stew-drafted document. Mirrors the exact
 * pattern the resolution wizard uses (ResolutionPhase1.handleSignaturesComplete):
 *
 *   1. Reuse FinalizeWithSignatures + SignatureCapture to capture the officer PNG.
 *   2. Embed the signature into the document HTML via embedSignaturesIntoHtml.
 *   3. Re-render a signed PDF through the shared generate-pdf-server function.
 *   4. Upload to the private `documents` bucket.
 *   5. Update generated_documents with status='finalized', finalized_at,
 *      finalized_by, file_url/file_path/file_size, signed_file_url,
 *      custom_content=updatedHtml, and signature_history.
 *
 * We also enforce multi-version safety: if a newer revision of this document
 * exists in the conversation, signing is blocked with a quiet notice.
 */

import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { FinalizeWithSignatures } from "@/components/documents/FinalizeWithSignatures";
import { embedSignaturesIntoHtml } from "@/lib/signatureEmbedding";
import { getFullDocumentHtml } from "@/lib/documentHtml";
import { generatedFilePath } from "@/components/workflows/docProvenance";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export interface StewSignTarget {
  id: string;
  document_name: string;
  document_type: string;
  una_id: string;
  version: number;
  registry_display_name: string;
}

interface Props {
  open: boolean;
  target: StewSignTarget | null;
  onCancel: () => void;
  onSigned: (target: StewSignTarget) => void;
}

const SIGN_DESCRIPTION =
  "This one becomes part of your permanent record once signed. Review it once more, then sign below.";

function base64ToBlob(base64: string, mimeType: string): Blob {
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mimeType });
}

export function StewSignFlow({ open, target, onCancel, onSigned }: Props) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [checking, setChecking] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);
  const [staleNotice, setStaleNotice] = useState<null | { latestId: string }>(
    null,
  );
  const [officer, setOfficer] = useState<{
    id: string;
    name: string;
    roleLabel: string;
  } | null>(null);

  // Verify latest-version safety and load the primary officer for the una.
  useEffect(() => {
    if (!open || !target) {
      setOfficer(null);
      setStaleNotice(null);
      setOpenError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setChecking(true);
      setOpenError(null);
      try {
        // Descendant check: any newer revision of this document?
        const { data: descendants } = await supabase
          .from("generated_documents")
          .select("id, version")
          .eq("parent_document_id", target.id)
          .order("version", { ascending: false })
          .limit(1);

        if (cancelled) return;
        if (descendants && descendants.length > 0) {
          setStaleNotice({ latestId: descendants[0].id });
          setChecking(false);
          return;
        }

        // Load primary officer info (treasury secretary + display role).
        const { data: una } = await supabase
          .from("unas")
          .select("treasury_secretary_id")
          .eq("id", target.una_id)
          .maybeSingle();

        const officerId = una?.treasury_secretary_id;
        if (!officerId) {
          throw new Error("Primary officer not found for this Association.");
        }

        const [profileRes, metaRes] = await Promise.all([
          supabase
            .from("profiles")
            .select("full_name")
            .eq("id", officerId)
            .maybeSingle(),
          (supabase as any)
            .from("una_metadata")
            .select("primary_officer_role")
            .eq("una_id", target.una_id)
            .maybeSingle(),
        ]);
        const profile = profileRes.data as { full_name?: string } | null;
        const meta = metaRes.data as { primary_officer_role?: string } | null;

        if (cancelled) return;
        setOfficer({
          id: officerId,
          name: profile?.full_name || "Primary Officer",
          roleLabel: meta?.primary_officer_role || "Secretary Treasurer",
        });
      } catch (e: any) {
        console.error("StewSignFlow open failed:", e);
        if (!cancelled) {
          setOpenError(
            "I couldn't open your signing pane just now. Close this and try again in a moment.",
          );
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, target]);

  const handleSignaturesComplete = useCallback(
    async (signatureResult: {
      treasury_secretary_signature?: string;
      treasury_secretary_signature_date?: string;
    }) => {
      if (!target || !officer || !user) return;
      const tsSignature = signatureResult.treasury_secretary_signature;
      const tsDate =
        signatureResult.treasury_secretary_signature_date ||
        new Date().toISOString();
      if (!tsSignature) {
        toast({
          title: "Signature required",
          description: "Please draw or type your signature to continue.",
          variant: "destructive",
        });
        return;
      }

      try {
        // Re-verify latest version at the very last moment (race safety).
        const { data: newer } = await supabase
          .from("generated_documents")
          .select("id")
          .eq("parent_document_id", target.id)
          .limit(1);
        if (newer && newer.length > 0) {
          setStaleNotice({ latestId: newer[0].id });
          return;
        }

        // Get the full HTML for the current document (uses shared pipeline:
        // custom_rendered_content -> custom_content -> compiled template).
        const originalHtml = await getFullDocumentHtml(target.id);

        // Embed the signature exactly the way the wizard does.
        const updatedHtml = await embedSignaturesIntoHtml(
          originalHtml,
          [
            {
              role: "treasury_secretary",
              signer_id: officer.id,
              signature_data: tsSignature,
              signed_at: tsDate,
            },
          ],
          new Date().toISOString(),
        );

        // Generate signed PDF via the shared generate-pdf-server function.
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) throw new Error("Session expired. Please sign in again.");

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-pdf-server`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ html: updatedHtml }),
          },
        );
        if (!response.ok) {
          const err = await response.json().catch(() => ({}) as any);
          throw new Error(err?.error || "Failed to generate signed PDF");
        }
        const { pdf: pdfBase64 } = await response.json();
        const pdfBlob = base64ToBlob(pdfBase64, "application/pdf");

        // Upload to the same private bucket the wizard uses.
        const filePath = generatedFilePath(null, `${target.document_name || "document"}_signed`, target.id);
        const { error: uploadError } = await supabase.storage
          .from("documents")
          .upload(filePath, pdfBlob, {
            contentType: "application/pdf",
            upsert: true,
          });
        if (uploadError) throw uploadError;

        // Update the document row with the same fields the wizard writes.
        const updatePayload: any = {
          status: "finalized",
          finalized_at: new Date().toISOString(),
          finalized_by: user.id,
          file_url: filePath,
          file_path: filePath,
          file_size: pdfBlob.size,
          signed_file_url: filePath,
          custom_content: updatedHtml,
          signature_history: {
            finalized_signatures: {
              treasury_secretary_signature: tsSignature,
              treasury_secretary_date: tsDate,
              finalized_at: new Date().toISOString(),
              signer_user_id: officer.id,
              signer_role: officer.roleLabel,
              origin: "stew",
            },
          },
        };
        const { error: updateError } = await supabase
          .from("generated_documents")
          .update(updatePayload)
          .eq("id", target.id);
        if (updateError) throw updateError;

        onSigned(target);
      } catch (e: any) {
        console.error("StewSignFlow signing failed:", e);
        toast({
          title: "Signing didn't finish",
          description:
            "The signature didn't save. Try once more, and if it keeps happening, open a ticket.",
          variant: "destructive",
        });
      }
    },
    [target, officer, user, toast, onSigned],
  );

  if (!open || !target) return null;

  if (staleNotice) {
    return (
      <AlertDialog open onOpenChange={(v) => !v && onCancel()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Newer version available</AlertDialogTitle>
            <AlertDialogDescription>
              This draft has been revised since you approved it. Open the newest
              draft in the conversation and sign that one instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={onCancel}>Got it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  if (openError) {
    return (
      <AlertDialog open onOpenChange={(v) => !v && onCancel()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Signing didn't open</AlertDialogTitle>
            <AlertDialogDescription>{openError}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={onCancel}>Close</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  if (checking || !officer) {
    return (
      <AlertDialog open onOpenChange={(v) => !v && onCancel()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Preparing your signature</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                One moment while I get the signing pane ready.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <FinalizeWithSignatures
      documentTitle={target.document_name}
      description={SIGN_DESCRIPTION}
      treasurySecretaryName={officer.name}
      primaryOfficerRoleLabel={officer.roleLabel}
      requiredSigners={["treasury_secretary"]}
      onFinalize={handleSignaturesComplete}
      onCancel={onCancel}
    />
  );
}
